import { useEffect, useState } from 'react';
import { api, fmtMoney } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Pencil, Trash2, Search, Package } from 'lucide-react';
import { toast } from 'sonner';
import { EmptyState } from '@/components/EmptyState';

const empty = { name: '', description: '', category: 'General', unit_price: 0, unit: 'unidad' };

export default function CatalogPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/catalog');
      setItems(r.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditingId(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => {
    setEditingId(it.id);
    setForm({ name: it.name, description: it.description || '', category: it.category || 'General', unit_price: it.unit_price, unit: it.unit || 'unidad' });
    setOpen(true);
  };

  const save = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) { toast.error('El nombre es requerido'); return; }
    setSaving(true);
    try {
      const payload = { ...form, unit_price: parseFloat(form.unit_price) || 0 };
      if (editingId) {
        await api.put(`/catalog/${editingId}`, payload);
        toast.success('Ítem actualizado');
      } else {
        await api.post('/catalog', payload);
        toast.success('Ítem creado');
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo guardar');
    } finally { setSaving(false); }
  };

  const remove = async (it) => {
    if (!window.confirm(`¿Eliminar "${it.name}"?`)) return;
    try {
      await api.delete(`/catalog/${it.id}`);
      toast.success('Ítem eliminado');
      load();
    } catch { toast.error('No se pudo eliminar'); }
  };

  const filtered = items.filter((i) =>
    [i.name, i.description, i.category].some((x) => (x || '').toLowerCase().includes(query.toLowerCase()))
  );

  return (
    <div>
      <PageHeader
        title="Catálogo"
        subtitle="Productos y servicios que ofreces."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary h-10" onClick={openCreate} data-testid="catalog-new-button">
                <Plus className="mr-1.5 h-4 w-4" /> Nuevo ítem
              </Button>
            </DialogTrigger>
            <DialogContent className="glass border-[var(--glass-border)] bg-[#0F1626]">
              <DialogHeader>
                <DialogTitle>{editingId ? 'Editar ítem' : 'Nuevo ítem de catálogo'}</DialogTitle>
              </DialogHeader>
              <form className="space-y-4" onSubmit={save}>
                <div className="space-y-1.5">
                  <Label htmlFor="name">Nombre</Label>
                  <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="catalog-form-name" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="description">Descripción</Label>
                  <Textarea id="description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="catalog-form-description" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="category">Categoría</Label>
                    <Input id="category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="catalog-form-category" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="unit">Unidad</Label>
                    <Input id="unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} data-testid="catalog-form-unit" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="unit_price">Precio (USD)</Label>
                    <Input id="unit_price" type="number" min="0" step="0.01" required value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} data-testid="catalog-form-price" />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
                  <Button type="submit" className="btn-primary" disabled={saving} data-testid="catalog-form-save">{saving ? 'Guardando…' : 'Guardar'}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="glass overflow-hidden rounded-xl">
        <div className="flex items-center gap-3 border-b border-[var(--glass-border)] p-3">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar por nombre, categoría…" className="h-9 border-0 bg-transparent focus-visible:ring-0" data-testid="catalog-search-input" />
          <span className="font-mono text-xs text-muted-foreground">{filtered.length} ítems</span>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Cargando…</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Package}
            title={query ? 'Sin resultados' : 'Catálogo vacío'}
            description={query ? 'Intenta otra búsqueda.' : 'Agrega tu primer producto o servicio.'}
            action={!query && <Button className="btn-primary" onClick={openCreate}><Plus className="mr-1.5 h-4 w-4" /> Crear ítem</Button>}
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Nombre</TableHead>
                <TableHead>Categoría</TableHead>
                <TableHead>Unidad</TableHead>
                <TableHead className="text-right">Precio (USD)</TableHead>
                <TableHead className="w-[100px] text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((it) => (
                <TableRow key={it.id} className="hover:bg-[var(--surface-1)]">
                  <TableCell>
                    <div className="font-medium">{it.name}</div>
                    {it.description && <div className="text-xs text-muted-foreground">{it.description}</div>}
                  </TableCell>
                  <TableCell><span className="text-sm text-muted-foreground">{it.category}</span></TableCell>
                  <TableCell><span className="text-sm">{it.unit}</span></TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{fmtMoney(it.unit_price)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(it)} data-testid={`catalog-edit-${it.name}`}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(it)} data-testid={`catalog-delete-${it.name}`} className="text-[#FF8FA3] hover:text-[#FF5C7A]"><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
